import React from 'react';
import { logger } from '../../utils/logger';

const ProgressBar = ({ 
  label, 
  current, 
  max, 
  unit = '', 
  color = 'blue',
  showPercentage = true,
  showNumbers = true,
  size = 'medium',
  numbersAddon = null,
  className = '' 
}) => {
  // Calculate percentage - handle undefined/null values
  const safeCurrent = current ?? 0;
  const safeMax = max ?? 0;
  const percentage = safeMax > 0 ? Math.min((safeCurrent / safeMax) * 100, 100) : 0;
  // logger.log("current", current);
  // logger.log("max", max);
  // logger.log("percentage", percentage);
  
  // Cast current, max to localeString if they are not already
  const current_localeString = (current != null && typeof current.toLocaleString === 'function') 
    ? current.toLocaleString() : (current ?? 0);
  const max_localeString = (max != null && typeof max.toLocaleString === 'function') 
    ? max.toLocaleString() : (max ?? 0);
  
  // Determine color scheme based on usage
  const getColorScheme = () => {
    if (percentage >= 90) return 'red';
    if (percentage >= 75) return 'yellow';
    return color;
  };
  
  const colorScheme = getColorScheme();
  
  // Color classes
  const colorClasses = {
    blue: {
      bg: 'bg-blue-100',
      bar: 'bg-blue-500',
      text: 'text-blue-700'
    },
    green: {
      bg: 'bg-green-100',
      bar: 'bg-green-500',
      text: 'text-green-700'
    },
    yellow: {
      bg: 'bg-yellow-100',
      bar: 'bg-yellow-500',
      text: 'text-yellow-700'
    },
    red: {
      bg: 'bg-red-100',
      bar: 'bg-red-500',
      text: 'text-red-700'
    },
    purple: {
      bg: 'bg-purple-100',
      bar: 'bg-purple-500',
      text: 'text-purple-700'
    }
  };
  
  // Size classes
  const sizeClasses = {
    small: 'h-2',
    medium: 'h-3',
    large: 'h-4'
  };
  
  const colors = colorClasses[colorScheme];
  const height = sizeClasses[size];

  return (
    <div className={`w-full ${className}`}>
      {/* Label and numbers */}
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex items-center gap-2 text-sm">
          {showNumbers && (
            <span className="text-gray-600">
              {current_localeString}/{max_localeString} {unit}
            </span>
          )}
          {numbersAddon}
          {showPercentage && (
            <span className={`font-semibold ${colors.text}`}>
              {percentage.toFixed(0)}%
            </span>
          )}
        </div>
      </div>
      
      {/* Progress bar */}
      <div className={`w-full ${colors.bg} rounded-full ${height} overflow-hidden`}>
        <div
          className={`${colors.bar} ${height} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${percentage}%` }}
        >
          {/* Animated shine effect */}
          <div className="h-full w-full bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
        </div>
      </div>
      
      {/* Usage status text */}
      {percentage >= 90 && (
        <div className="mt-1 text-xs text-red-600 font-medium">
          {percentage >= 100 ? 'Limit reached' : 'Approaching limit'}
        </div>
      )}
    </div>
  );
};

export default ProgressBar; 